import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback, Fragment } from 'react';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { AdminOnlyEmptyState } from '@/components/auth/admin-only-empty-state';
import { sanitizeTemplateContent, sanitizeTemplateSubject } from '@/lib/sanitize';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ScrollArea, ScrollAreaViewport, ScrollBar } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { 
  ArrowLeft, 
  Eye, 
  Mail,
  Loader2
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Template } from '@/types/template';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { TemplateVariablesPanel, TEMPLATE_VARIABLES } from '@/components/template-variables-panel';
import { findUnknownVariables, findDisallowedSubjectVariables, convertVariablesToNodes, extractPlaceholderKeys } from '@/lib/email-placeholder-utils';
import { getAllowedSections, findDisallowedVariables } from '@/lib/template-category-variables';
import type { Editor } from '@tiptap/react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { VariableExtension } from '@/lib/tiptap-variable-extension';
import { mapTemplateDetailApiToUi } from '@/lib/mappers/templates';
import { toast } from 'sonner';
import { useTemplateById, useUpdateTemplate, useCreateTemplate, useTemplateCategoryTypes } from '@/hooks/useTemplatesData';
import { useCompany } from '@/hooks/use-company';
import { useCompanyWithProfile } from '@/hooks/useCompanyWithProfile';


const convertVariableNodesToText = (html: string): string => {
  return html
    .replace(/<span[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/g, '$1')
    .replace(/<\/?p>/g, '')
    .trim();
};

type EditorDropHandler = NonNullable<NonNullable<Parameters<typeof useEditor>[0]>['editorProps']>['handleDrop'];

function createSubjectEditorDrop(
  setIsSubjectDragOver: (value: boolean) => void,
): NonNullable<EditorDropHandler> {
  return (view, event) => {
    // Check if this is a template variable drop
    const variable = event.dataTransfer?.getData('application/x-template-variable');
    if (variable) {
      event.preventDefault();
      event.stopPropagation();

      // Clear the drag over state immediately
      setIsSubjectDragOver(false);

      console.log('Subject editor - dropping variable:', variable);

      // Get the position where the drop occurred
      const coords = { left: event.clientX, top: event.clientY };
      const pos = view.posAtCoords(coords);

      console.log('Subject editor - drop position:', pos);

      if (pos) {
        // Get the editor instance from the view
        const { state, dispatch } = view;

        // Create the variable node
        const variableNode = state.schema.nodes.variable.create({ variable });

        // Insert at the drop position
        const transaction = state.tr.insert(pos.pos, variableNode);
        dispatch(transaction);

        console.log('Subject editor - variable inserted at position:', pos.pos);
      }
      return true;
    }
    return false;
  };
}

function subjectEditorDragOver(_view: unknown, event: DragEvent): boolean {
  // Check if this is a template variable
  const types = event.dataTransfer?.types || [];
  if (types.includes('application/x-template-variable')) {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'copy';
    return true;
  }
  return false;
}

interface TemplateEditorPageProps {
  /** When rendered inside a parent component (e.g. onboarding wizard) supply these
   *  callbacks so the editor can signal back without using the router. When absent
   *  the component falls back to `navigate` as before. */
  inlineMode?: {
    template?: Template;
    mode: 'create' | 'edit';
    duplicateFromId?: string;
    onBack: () => void;
    onSaved: (savedTemplate: Template, mode: 'create' | 'edit') => void;
  };
}

export function TemplateEditorPage({ inlineMode }: TemplateEditorPageProps = {}) {
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();

  // When running inside a parent component use the inlineMode props, otherwise
  // fall back to reading from router location.state (normal routed behaviour).
  const locationTemplate = inlineMode
    ? inlineMode.template
    : (location.state?.template as Template | undefined);
  const isEditMode = inlineMode ? inlineMode.mode === 'edit' : location.state?.mode === 'edit';
  const duplicateFromId = inlineMode
    ? inlineMode.duplicateFromId
    : (location.state?.duplicateFromId as string | undefined);
  const isDuplicateMode = !!duplicateFromId;
  const fetchId = isEditMode ? (locationTemplate?.id ?? null) : (duplicateFromId ?? null);

  const { data: fetchedTemplate, isLoading: isLoadingTemplate } = useTemplateById(fetchId);
  const template = (() => {
    if (isEditMode) return fetchedTemplate ?? locationTemplate;
    if (isDuplicateMode && fetchedTemplate) {
      return { ...fetchedTemplate, name: locationTemplate?.name ?? `${fetchedTemplate.name} (COPY)` };
    }
    return locationTemplate;
  })();
  const updateTemplateMutation = useUpdateTemplate(fetchId ?? '', { forceLive: !!inlineMode });
  const createTemplateMutation = useCreateTemplate({ forceLive: !!inlineMode });
  const [isSaving, setIsSaving] = useState(false);

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const contentEditorRef = useRef<Editor | null>(null);
  const [type] = useState<'email' | 'statement'>(template?.type || 'email');
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [lastFocusedEditor, setLastFocusedEditor] = useState<'subject' | 'content'>('content');
  const [subjectCursorPosition, setSubjectCursorPosition] = useState<number | null>(null);
  const [contentCursorPosition, setContentCursorPosition] = useState<number | null>(null);
  const [categoryTypeId, setCategoryTypeId] = useState<number | undefined>(undefined);
  const [attachInvoiceCopies, setAttachInvoiceCopies] = useState(false);
  const [isSubjectDragOver, setIsSubjectDragOver] = useState(false);

  const { data: categoryTypes } = useTemplateCategoryTypes();
  const { selectedCompany } = useCompany();
  const { data: companyWithProfile } = useCompanyWithProfile(selectedCompany?.id);
  const companyLogoUrl = companyWithProfile?.details?.logoUrl ?? selectedCompany?.logoUrl ?? null;

  // Derive the category code from the selected categoryTypeId so we can
  // restrict the variables panel and validate on save.
  const selectedCategoryCode = categoryTypes?.find(c => c.id === categoryTypeId)?.code;
  const allowedSections = getAllowedSections(selectedCategoryCode);
  
  // Store initial values to detect changes
  const [initialName, setInitialName] = useState('');
  const [initialSubject, setInitialSubject] = useState('');
  const [initialContent, setInitialContent] = useState('');
  const [initialCategoryTypeId, setInitialCategoryTypeId] = useState<number | undefined>(undefined);
  const [initialAttachInvoiceCopies, setInitialAttachInvoiceCopies] = useState(false);
  
  // Cancel confirmation dialog
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const pendingContentRef = useRef<string>('');
  const initializedFromRef = useRef<Template | null>(null);

  const handleContentEditorReady = useCallback((editor: Editor) => {
    contentEditorRef.current = editor;
    if (pendingContentRef.current) {
      editor.commands.setContent(pendingContentRef.current);
    }
  }, []);

  const handleContentEditorFocus = useCallback(() => {
    setLastFocusedEditor('content');
  }, []);

  const handleContentSelectionUpdate = useCallback((position: number) => {
    setContentCursorPosition(position);
  }, []);

  const handleContentEditorBlur = useCallback((position: number) => {
    setContentCursorPosition(position);
  }, []);

  // Subject editor (single line)
  const subjectEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: 'Enter email subject...',
      }),
      VariableExtension,
    ],
    content: subject,
    editorProps: {
      handleDrop: createSubjectEditorDrop(setIsSubjectDragOver),
      handleDOMEvents: {
        dragover: subjectEditorDragOver,
      },
    },
    onUpdate: ({ editor }) => {
      const text = editor.getText();
      setSubject(text);
    },
    onFocus: () => {
      setLastFocusedEditor('subject');
    },
    onSelectionUpdate: ({ editor }) => {
      // Store cursor position whenever it changes
      const pos = editor.state.selection.from;
      setSubjectCursorPosition(pos);
    },
    onBlur: ({ editor }) => {
      // Store cursor position when editor loses focus
      const pos = editor.state.selection.from;
      setSubjectCursorPosition(pos);
    },
  });

  useEffect(() => {
    if (isEditMode && !locationTemplate && !isLoadingTemplate && !fetchedTemplate) {
      if (inlineMode) {
        inlineMode.onBack();
      } else {
        navigate('/settings/templates');
      }
    }
  }, [isEditMode, locationTemplate, isLoadingTemplate, fetchedTemplate, navigate, inlineMode]);

  useEffect(() => {
    const sourceTemplate = (isEditMode || isDuplicateMode) ? fetchedTemplate : locationTemplate;
    if (!sourceTemplate) return;
    if ((isEditMode || isDuplicateMode) && !fetchedTemplate) return;
    if (sourceTemplate === initializedFromRef.current) return;

    const isReinit = initializedFromRef.current !== null;
    initializedFromRef.current = sourceTemplate;

    const convertedSubject = convertVariablesToNodes(sourceTemplate.subject || '');
    const convertedContent = convertVariablesToNodes(sourceTemplate.content);

    const effectiveName = isDuplicateMode
      ? (locationTemplate?.name ?? `${sourceTemplate.name} (COPY)`)
      : sourceTemplate.name;

    setName(effectiveName);
    setSubject(convertedSubject);
    setContent(convertedContent);
    setCategoryTypeId(sourceTemplate.categoryTypeId);
    setAttachInvoiceCopies(sourceTemplate.attachInvoiceCopies || false);

    setInitialName(effectiveName);
    setInitialSubject(convertedSubject);
    setInitialContent(convertedContent);
    setInitialCategoryTypeId(sourceTemplate.categoryTypeId);
    setInitialAttachInvoiceCopies(sourceTemplate.attachInvoiceCopies || false);

    pendingContentRef.current = convertedContent;

    if (isReinit) {
      if (contentEditorRef.current && !contentEditorRef.current.isDestroyed) {
        contentEditorRef.current.commands.setContent(convertedContent);
      }
      if (subjectEditor && !subjectEditor.isDestroyed) {
        subjectEditor.commands.setContent(convertedSubject);
      }
    }
  }, [fetchedTemplate, locationTemplate, isEditMode, isDuplicateMode, subjectEditor]);

  useEffect(() => {
    if (!subjectEditor || subjectEditor.isDestroyed || !initializedFromRef.current) return;
    if (subjectEditor.getHTML() === '<p></p>' && subject) {
      subjectEditor.commands.setContent(subject);
    }
  }, [subjectEditor, subject]);

  const insertIntoSubjectEditor = (variable: string) => {
    const isEditorReady = subjectEditor?.view && !subjectEditor.isDestroyed;
    if (!isEditorReady) return;
    try {
      subjectEditor.commands.focus();
      if (subjectCursorPosition !== null) {
        subjectEditor.commands.setTextSelection(subjectCursorPosition);
      }
      (subjectEditor.commands as any).insertVariable(variable);
    } catch (error) {
      console.error('Error inserting into subject editor:', error);
    }
  };

  const insertIntoContentEditor = (variable: string) => {
    const editor = contentEditorRef.current;
    const isEditorReady = editor?.view && !editor.isDestroyed;
    if (!isEditorReady) return false;
    try {
      editor.commands.focus();
      if (contentCursorPosition !== null) {
        editor.commands.setTextSelection(contentCursorPosition);
      }
      (editor.commands as any).insertVariable(variable);
      return true;
    } catch (error) {
      console.error('Error inserting via editor commands:', error);
      return false;
    }
  };

  const insertVariableViaDomFallback = (variable: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content || '<p></p>', 'text/html');

    const variableSpan = doc.createElement('span');
    variableSpan.setAttribute('data-variable', variable);
    variableSpan.className = 'variable-node';

    const targetPos = contentCursorPosition ?? 0;
    let currentPos = 0;
    let targetNode: Node | null = null;
    let offsetInNode = 0;

    const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null);
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const textLength = node.textContent?.length || 0;
      if (currentPos + textLength >= targetPos) {
        targetNode = node;
        offsetInNode = targetPos - currentPos;
        break;
      }
      currentPos += textLength;
    }

    if (targetNode && targetNode.parentNode) {
      const text = targetNode.textContent || '';
      if (offsetInNode > 0) {
        const beforeText = doc.createTextNode(text.substring(0, offsetInNode));
        const afterText = doc.createTextNode(text.substring(offsetInNode));
        targetNode.parentNode.insertBefore(beforeText, targetNode);
        targetNode.parentNode.insertBefore(variableSpan, targetNode);
        targetNode.parentNode.insertBefore(afterText, targetNode);
        targetNode.parentNode.removeChild(targetNode);
      } else {
        targetNode.parentNode.insertBefore(variableSpan, targetNode);
      }
    } else {
      const lastP = doc.body.querySelector('p:last-child');
      if (lastP) {
        lastP.appendChild(variableSpan);
      } else {
        const p = doc.createElement('p');
        p.appendChild(variableSpan);
        doc.body.appendChild(p);
      }
    }

    setContent(doc.body.innerHTML);
  };

  const handleInsertVariable = (variable: string) => {
    if (lastFocusedEditor === 'subject' && subjectEditor) {
      insertIntoSubjectEditor(variable);
    } else {
      const insertedViaEditor = insertIntoContentEditor(variable);
      if (!insertedViaEditor) {
        insertVariableViaDomFallback(variable);
      }
    }
  };

  const allowedVariableKeys = new Set(
    TEMPLATE_VARIABLES.map((v) => v.variable.replace(/^<<|>>$/g, '')),
  );

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    const subjectHtml = subjectEditor?.getHTML() || '';
    const subjectContent = convertVariableNodesToText(subjectHtml);
    const bodyContent = content || contentEditorRef.current?.getHTML() || '';

    if (!subjectContent.trim()) {
      toast.error('Please enter a subject line');
      return;
    }

    if (!bodyContent.trim()) {
      toast.error('Please enter template content');
      return;
    }

    // Hard-block: reject templates that use <<companyLogo>> without a configured logo
    const allUsedKeys = extractPlaceholderKeys(subjectContent + bodyContent);
    if (allUsedKeys.some(k => k.toLowerCase() === 'companylogo') && !companyLogoUrl) {
      toast.error('This template uses <<companyLogo>> but no company logo is configured. Go to Company Settings to upload a logo before saving.');
      return;
    }

    // Hard-block: reject templates that contain unknown variables
    const unknownInSubject = findUnknownVariables(subjectContent, allowedVariableKeys);
    const unknownInBody = findUnknownVariables(bodyContent, allowedVariableKeys);
    const allUnknown = [...new Set([...unknownInSubject, ...unknownInBody])];
    if (allUnknown.length > 0) {
      toast.error(
        `Template contains unknown variables: ${allUnknown.map((k) => `<<${k}>>`).join(', ')}. Please use only predefined variables from the Variables panel.`,
      );
      return;
    }

    // Hard-block: reject HTML-producing variables in subject line
    const disallowedInSubject = findDisallowedSubjectVariables(subjectContent);
    if (disallowedInSubject.length > 0) {
      toast.error(
        `The following variables cannot be used in the subject line because they produce complex HTML: ${disallowedInSubject.map((k) => `<<${k}>>`).join(', ')}. Please use them only in the email body.`,
      );
      return;
    }

    // Hard-block: reject variables that are not allowed for the selected category
    if (selectedCategoryCode) {
      const disallowed = findDisallowedVariables(selectedCategoryCode, subjectContent, bodyContent);
      if (disallowed.length > 0) {
        const categoryName = categoryTypes?.find(c => c.code === selectedCategoryCode)?.name ?? selectedCategoryCode;
        toast.error(
          `The following variables are not allowed for the ${categoryName} category: ${disallowed.join(', ')}. Please remove them or change the category.`,
        );
        return;
      }
    }

    if (isEditMode && template) {
      const templateData: Template = {
        ...template,
        name,
        subject: subjectContent,
        content: bodyContent,
        type,
        categoryTypeId,
        attachInvoiceCopies,
      };

      setIsSaving(true);
      updateTemplateMutation.mutate(templateData, {
        onSuccess: (apiResponse) => {
          setIsSaving(false);
          const savedTemplate = mapTemplateDetailApiToUi(apiResponse);
          toast.success('Template saved successfully');
          if (inlineMode) {
            inlineMode.onSaved(savedTemplate, 'edit');
          } else {
            navigate('/settings/templates', { state: { savedTemplate, mode: 'edit' } });
          }
        },
        onError: (error) => {
          setIsSaving(false);
          toast.error(`Failed to save template: ${error.message}`);
        },
      });
    } else {
      const templateData: Template = {
        id: '',
        name,
        subject: subjectContent,
        content: bodyContent,
        type,
        isDefault: false,
        category: [],
        categoryTypeId,
        variables: [],
        lastModified: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        createdBy: '',
        attachInvoiceCopies,
      };

      setIsSaving(true);
      createTemplateMutation.mutate(templateData, {
        onSuccess: (apiTemplate) => {
          setIsSaving(false);
          const savedTemplate = mapTemplateDetailApiToUi(apiTemplate);
          toast.success('Template created successfully');
          if (inlineMode) {
            inlineMode.onSaved(savedTemplate, 'create');
          } else {
            navigate('/settings/templates', { state: { savedTemplate, mode: 'create' } });
          }
        },
        onError: (error) => {
          setIsSaving(false);
          toast.error(`Failed to create template: ${error.message}`);
        },
      });
    }
  };

  const handleCancel = () => {
    // Get current editor content (HTML for both subject and content)
    const currentSubject = subjectEditor?.getHTML() || subject;
    const currentContent = contentEditorRef.current?.getHTML() || content;
    
    // Normalize HTML for comparison (remove extra whitespace, etc.)
    const normalizeHtml = (html: string) => {
      return html.replace(/\s+/g, ' ').trim();
    };
    
    const normalizedCurrentSubject = normalizeHtml(currentSubject);
    const normalizedInitialSubject = normalizeHtml(initialSubject);
    const normalizedCurrentContent = normalizeHtml(currentContent);
    const normalizedInitialContent = normalizeHtml(initialContent);
    
    // Check if there are actual changes
    const hasChanges = 
      name.trim() !== initialName.trim() ||
      normalizedCurrentSubject !== normalizedInitialSubject ||
      normalizedCurrentContent !== normalizedInitialContent ||
      categoryTypeId !== initialCategoryTypeId ||
      attachInvoiceCopies !== initialAttachInvoiceCopies;

    if (hasChanges) {
      setShowCancelDialog(true);
    } else {
      if (inlineMode) {
        inlineMode.onBack();
      } else {
        navigate('/settings/templates');
      }
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelDialog(false);
    if (inlineMode) {
      inlineMode.onBack();
    } else {
      navigate('/settings/templates');
    }
  };

  const handlePreview = () => {
    // Use the content state directly (it's already HTML from the editor)
    // Prioritize the state variables as they contain the actual content
    const previewContentRaw = content || contentEditorRef.current?.getHTML() || '';
    const previewSubjectRaw = subject || subjectEditor?.getHTML() || '';
    
    // Sample data for variable replacement (camelCase format)
    const sampleData: Record<string, string> = {
      // Customer Information
      '<<customerName>>': 'John Smith',
      '<<customerEmail>>': 'john.smith@acme.com',
      '<<customerPhone>>': '+44 20 7946 0301',
      '<<accountNumber>>': 'ACC-12345',
      '<<contactFirstName>>': 'Jane',
      '<<contactSurname>>': 'Doe',
      '<<contactEmail>>': 'jane.doe@acme.com',
      '<<contactPhone>>': '+44 20 7946 0399',
      '<<accountBalance>>': '£15,750.00',
      '<<overdueAmount>>': '£5,250.00',
      '<<creditLimit>>': '£50,000.00',
      '<<paymentTerms>>': 'Net 30',
      '<<dueDays>>': '30',
      '<<dueDaysType>>': 'Days from Invoice',

      // Transaction Details
      '<<outstandingTransactions>>': `<table style="width: 100% !important; border-collapse: collapse !important; margin: 16px 0 !important; font-size: 14px !important; line-height: 1.5 !important; border: 1px solid #dee2e6 !important;">
        <thead>
          <tr style="background-color: #f8f9fa !important; border-bottom: 2px solid #dee2e6 !important;">
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Reference</th>
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Date</th>
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Due Date</th>
            <th style="padding: 12px 16px !important; text-align: right !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Gross</th>
            <th style="padding: 12px 16px !important; text-align: right !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Outstanding</th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom: 1px solid #dee2e6 !important;">
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">INV-2024-003</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Jan 20, 2024</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Feb 20, 2024</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">£3,200.00</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">£3,200.00</td>
          </tr>
          <tr style="border-bottom: 1px solid #dee2e6 !important;">
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">INV-2024-002</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Jan 18, 2024</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Feb 18, 2024</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">£7,800.00</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">£7,800.00</td>
          </tr>
          <tr style="border-bottom: 1px solid #dee2e6 !important;">
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">INV-2024-001</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Jan 15, 2024</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Feb 15, 2024</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">£5,250.00</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">£5,250.00</td>
          </tr>
        </tbody>
      </table>`,
      '<<overdueTransactions>>': `<table style="width: 100% !important; border-collapse: collapse !important; margin: 16px 0 !important; font-size: 14px !important; line-height: 1.5 !important; border: 1px solid #dee2e6 !important;">
        <thead>
          <tr style="background-color: #f8f9fa !important; border-bottom: 2px solid #dee2e6 !important;">
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Reference</th>
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Date</th>
            <th style="padding: 12px 16px !important; text-align: left !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Due Date</th>
            <th style="padding: 12px 16px !important; text-align: right !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Gross</th>
            <th style="padding: 12px 16px !important; text-align: right !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Outstanding</th>
            <th style="padding: 12px 16px !important; text-align: right !important; font-weight: 600 !important; color: #212529 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Days Overdue</th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom: 1px solid #dee2e6 !important;">
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">INV-2024-001</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Jan 15, 2024</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Feb 15, 2024</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">£5,250.00</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">£5,250.00</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">72</td>
          </tr>
          <tr style="border-bottom: 1px solid #dee2e6 !important;">
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">INV-2023-089</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Dec 10, 2023</td>
            <td style="padding: 12px 16px !important; text-align: left !important; color: #495057 !important; border: 1px solid #dee2e6 !important; white-space: nowrap !important;">Jan 10, 2024</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">£10,500.00</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">£10,500.00</td>
            <td style="padding: 12px 16px !important; text-align: right !important; color: #495057 !important; border: 1px solid #dee2e6 !important; font-weight: 500 !important; white-space: nowrap !important;">107</td>
          </tr>
        </tbody>
      </table>`,
      
      // Company Information
      '<<companyName>>': 'Acme Corporation',
      '<<companyEmail>>': 'info@acme.com',
      '<<companyPhone>>': '+44 800 123 4567',
      '<<supportEmail>>': '',
      '<<creditController>>': 'Michael Johnson',
      '<<creditControllerFirstName>>': 'Michael',
      '<<creditControllerLastName>>': 'Johnson',
      '<<creditControllerEmail>>': 'michael.johnson@acme.com',
      '<<companyLogo>>': companyLogoUrl
        ? '__COMPANY_LOGO_PLACEHOLDER__'
        : '',
      '<<companyBankName>>': 'Barclays Bank PLC',
      '<<sortCode>>': '20-45-67',
      '<<bankAccountNumber>>': '12345678',
      '<<ibanNumber>>': 'GB29NWBK60161331926819',
      '<<swiftBicCode>>': 'NWBKGB2L',
      
      // Payment Information
      '<<promisedAmount>>': '£5,250.00',
      '<<promiseDate>>': 'February 28, 2024',
      
      // System Information
      '<<systemDate>>': new Date().toLocaleDateString('en-GB'),
      '<<systemTime>>': new Date().toLocaleTimeString('en-GB'),
    };

    // Helper function to replace variables in HTML
    const replaceVariablesInHtml = (html: string): string => {
      if (!html || html.trim() === '') {
        return html;
      }
      
      // Create a temporary div to work with the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Find all variable spans
      const variableSpans = tempDiv.querySelectorAll('span[data-variable]');
      
      variableSpans.forEach((span) => {
        const variable = span.getAttribute('data-variable');
        if (variable && sampleData[variable]) {
          // Check if the variable value contains HTML (like caseTransactions table)
          if (sampleData[variable].includes('<')) {
            // For HTML content, create a temporary container and set innerHTML
            const htmlContainer = document.createElement('div');
            htmlContainer.innerHTML = sampleData[variable];
            // Replace the span with all children of the container
            while (htmlContainer.firstChild) {
              span.parentNode?.insertBefore(htmlContainer.firstChild, span);
            }
            span.parentNode?.removeChild(span);
          } else {
            // For plain text, create a strong element with the sample value
            const strong = document.createElement('strong');
            strong.textContent = sampleData[variable];
            // Replace the span with the strong element
            span.parentNode?.replaceChild(strong, span);
          }
        }
      });
      
      // Return the modified HTML
      return tempDiv.innerHTML;
    };

    // Replace variables with sample data for preview
    const previewContent = replaceVariablesInHtml(previewContentRaw);
    const previewSubject = replaceVariablesInHtml(previewSubjectRaw);
    
    console.log('Preview Content Raw:', previewContentRaw);
    console.log('Preview Content Processed:', previewContent);
    console.log('Preview Subject Raw:', previewSubjectRaw);
    console.log('Preview Subject Processed:', previewSubject);

    // Store as JSON string with both subject and content
    const previewData = JSON.stringify({
      subject: previewSubject,
      content: previewContent
    });

    console.log('Preview Data JSON:', previewData);

    setPreviewHtml(previewData);
    setShowPreview(true);

    const allContent = previewContentRaw + previewSubjectRaw;
    const usedKeys = extractPlaceholderKeys(allContent);
    if (usedKeys.some(k => k.toLowerCase() === 'companylogo') && !companyLogoUrl) {
      toast.warning('No company logo is configured. Go to Company Settings to upload a logo.');
    }
  };

  if (isAdminLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <output
          aria-label="Loading"
          className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        />
      </div>
    );
  }
  if (!isAdmin) return <AdminOnlyEmptyState />;

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="border-b bg-background">
        <div className="container mx-auto py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCancel}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold">
                  {isEditMode ? 'Edit Template' : 'Create Template'}
                </h1>
                <p className="text-muted-foreground">
                  {isEditMode ? 'Modify your template' : 'Create a new template'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="outline" onClick={handlePreview}>
                Preview
              </Button>
              <Button onClick={handleSave} disabled={isSaving || isLoadingTemplate}>
                {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Two-Column Layout */}
      <div className="flex-1 overflow-hidden">
        <div className="container mx-auto h-full">
          {isEditMode && isLoadingTemplate ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground">Loading template...</p>
              </div>
            </div>
          ) : (
          <div className="flex flex-row gap-3 h-full py-6">
            {/* Left Column - Template Details */}
            <div className="flex-[7] overflow-y-auto pr-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
              {/* Template Details Card */}
              <Card className="h-fit">
                <CardHeader>
                  <CardTitle>Template Details</CardTitle>
                  <CardDescription>
                    {type === 'statement' && 'Create an email statement template with rich text formatting'}
                    {type === 'email' && 'Create an email template with rich text formatting'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Template Name</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter template name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={categoryTypeId !== undefined ? String(categoryTypeId) : ""}
                      onValueChange={(value) => setCategoryTypeId(value ? Number(value) : undefined)}
                    >
                      <SelectTrigger id="category">
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {(categoryTypes ?? []).map((cat) => (
                          <SelectItem key={cat.id} value={String(cat.id)}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Subject Line */}
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject Line</Label>
                    {subjectEditor ? (
                      <div 
                        className={`border rounded-md transition-all relative ${
                          isSubjectDragOver ? 'border-primary border-2 ring-2 ring-primary/20' : ''
                        }`}
                        onDragEnter={(e) => {
                          const types = e.dataTransfer?.types || [];
                          if (types.includes('application/x-template-variable')) {
                            e.preventDefault();
                            setIsSubjectDragOver(true);
                          }
                        }}
                        onDragLeave={(e) => {
                          // Check if we're leaving the editor container (not just entering a child)
                          const rect = e.currentTarget.getBoundingClientRect();
                          const x = e.clientX;
                          const y = e.clientY;
                          
                          // Only clear if mouse is actually outside the container bounds
                          if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
                            setIsSubjectDragOver(false);
                          }
                        }}
                        onDragOver={(e) => {
                          const types = e.dataTransfer?.types || [];
                          if (types.includes('application/x-template-variable')) {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'copy';
                          }
                        }}
                        onDrop={() => {
                          // Always clear the drag over state when dropping
                          setIsSubjectDragOver(false);
                        }}
                      >
                        <EditorContent 
                          editor={subjectEditor} 
                          className="prose prose-sm max-w-none p-3 min-h-[44px] focus-within:outline-none"
                        />
                        {isSubjectDragOver && (
                          <div className="absolute inset-0 bg-primary/5 pointer-events-none flex items-center justify-center">
                            <div className="bg-background border-2 border-primary rounded-lg px-3 py-1.5 shadow-lg">
                              <p className="text-xs font-medium text-primary">Drop variable here</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="border rounded-md p-3 min-h-[44px] text-muted-foreground">
                        Loading editor...
                      </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div className="space-y-2">
                    <Label htmlFor="content">Content</Label>
                    <RichTextEditor
                      content={content}
                      onChange={setContent}
                      onEditorReady={handleContentEditorReady}
                      onFocus={handleContentEditorFocus}
                      onSelectionUpdate={handleContentSelectionUpdate}
                      onBlur={handleContentEditorBlur}
                      placeholder="Enter template content..."
                    />
                  </div>

                  <div className="flex items-start space-x-3 pt-2">
                    <Checkbox
                      id="attachInvoiceCopies"
                      checked={attachInvoiceCopies}
                      onCheckedChange={(checked) => setAttachInvoiceCopies(checked === true)}
                    />
                    <div className="grid gap-1.5 leading-none">
                      <Label
                        htmlFor="attachInvoiceCopies"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        Attach invoice copies to emails
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically attach invoice documents when sending emails using this template
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Variables Panel */}
            <div className="flex-[3] overflow-y-auto pl-3 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
              <Card className="h-fit">
                <CardHeader>
                  <CardTitle>Available Variables</CardTitle>
                </CardHeader>
                <CardContent>
                  <TemplateVariablesPanel onInsertVariable={handleInsertVariable} allowedSections={allowedSections} />
                </CardContent>
              </Card>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Enhanced Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={(open) => {
        setShowPreview(open);
        if (!open) {
          // Clear preview data when dialog closes
          setPreviewHtml('');
        }
      }}>
        <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <Eye className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <DialogTitle className="text-2xl font-semibold">Template Preview</DialogTitle>
                  <DialogDescription className="text-sm mt-1">
                    Preview how your template will appear with sample data
                  </DialogDescription>
                </div>
              </div>
              <Badge variant="secondary" className="flex items-center gap-1.5 px-3 py-1">
                {type === 'email' ? (
                  <>
                    <Mail className="h-3.5 w-3.5" />
                    <span>Email Template</span>
                  </>
                ) : (
                  <>
                    <Mail className="h-3.5 w-3.5" />
                    <span>Statement - Email</span>
                  </>
                )}
              </Badge>
            </div>
          </DialogHeader>
          
          <Separator />
          
          <div className="flex-1 flex flex-col px-6 py-6 overflow-hidden min-h-0 gap-6">
              {(() => {
                try {
                  if (!previewHtml || previewHtml.trim() === '') {
                    return (
                      <div className="text-center text-muted-foreground py-8">
                        No preview data available
                      </div>
                    );
                  }
                  
                  const previewData = JSON.parse(previewHtml);
                  const sanitizedSubject = sanitizeTemplateSubject(previewData.subject);
                  const sanitizedContent = sanitizeTemplateContent(previewData.content);

                  const LOGO_MARKER = '__COMPANY_LOGO_PLACEHOLDER__';
                  const contentParts = sanitizedContent.split(LOGO_MARKER);

                  const proseClasses = "prose prose-sm max-w-none prose-headings:text-foreground prose-headings:font-semibold prose-p:text-foreground prose-p:leading-relaxed prose-strong:text-foreground prose-strong:font-semibold prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-ul:text-foreground prose-ol:text-foreground prose-li:text-foreground prose-li:marker:text-muted-foreground prose-table:border-collapse prose-table:w-full prose-th:bg-muted/50 prose-th:border prose-th:border-border prose-th:px-4 prose-th:py-2 prose-th:text-left prose-th:font-semibold prose-td:border prose-td:border-border prose-td:px-4 prose-td:py-2 prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground prose-code:text-foreground prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-muted prose-pre:text-foreground";
                  
                  return (
                    <>
                      {type === 'email' && sanitizedSubject && (
                        <div className="flex-shrink-0 p-4 bg-muted/50 rounded-lg border border-border">
                          <div className="flex items-start gap-3">
                            <Mail className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-muted-foreground mb-1">Subject Line</div>
                              <div 
                                className="text-base font-medium text-foreground break-words"
                                dangerouslySetInnerHTML={{ __html: sanitizedSubject }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="bg-card rounded-lg border border-border shadow-sm flex-1 flex flex-col overflow-hidden min-h-0">
                        <div className="flex-shrink-0 bg-muted/30 px-4 py-2 border-b border-border">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            Template Content
                          </div>
                        </div>
                        <ScrollArea className="flex-1 min-h-0">
                          <ScrollAreaViewport className="h-full">
                            <div className="p-6">
                              {contentParts.length > 1 ? (
                                <div className={proseClasses}>
                                  {contentParts.map((part, index) => (
                                    <Fragment key={index}>
                                      {part && <div dangerouslySetInnerHTML={{ __html: part }} />}
                                      {index < contentParts.length - 1 && companyLogoUrl && (
                                        <div style={{ marginBottom: '16px' }}>
                                          <img
                                            src={companyLogoUrl}
                                            alt={companyWithProfile?.name || 'Company Logo'}
                                            style={{ maxHeight: '60px', maxWidth: '200px', objectFit: 'contain' }}
                                          />
                                        </div>
                                      )}
                                    </Fragment>
                                  ))}
                                </div>
                              ) : (
                                <div
                                  className={proseClasses}
                                  dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                                />
                              )}
                            </div>
                          </ScrollAreaViewport>
                          <ScrollBar orientation="vertical" />
                        </ScrollArea>
                      </div>
                    </>
                  );
                } catch (error) {
                  console.error('Error parsing preview data:', error);
                  return (
                    <div className="p-4 bg-destructive/10 text-destructive rounded-lg">
                      <p className="font-medium">Error displaying preview</p>
                      <p className="text-sm mt-1">Unable to parse preview data. Please try again.</p>
                    </div>
                  );
                }
              })()}
              
              <div className="flex-shrink-0 p-4 bg-accent/50 rounded-lg border border-accent-foreground/10">
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-accent flex-shrink-0">
                    <span className="text-xs font-semibold text-accent-foreground">ℹ</span>
                  </div>
                  <div className="flex-1 text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Preview Information</p>
                    <p>This preview shows your template with sample data. Variable placeholders have been replaced with example values to demonstrate how the final output will appear to recipients.</p>
                  </div>
                </div>
              </div>
          </div>
          
          <Separator />
          
          <DialogFooter className="px-6 py-4">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close Preview
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel? Any unsaved changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue Editing</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCancel}>
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
